import {CalendarWorkDay, CalendarActual, readWorkforceCalendar} from '../../workforce-v2/workforce-calendar';
import {EmployeeDurationPipe} from '../../employee/employee-duration.pipe';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { Dialog } from '@capacitor/dialog';
import { IonModal } from '@ionic/angular';
import { WorkforceService } from '../../workforce-v2/workforce.service';
import { PlanningPanelComponent } from '../../workforce-v2/planning-panel.component';
import { ModalShellComponent } from '../../ui/modal-shell/modal-shell.component';
import { TranslateService } from '@ngx-translate/core';
import { addWeeks, eachDayOfInterval, endOfWeek, format, isToday, isWeekend, startOfToday, startOfWeek, subWeeks } from 'date-fns';
import { fi } from 'date-fns/locale';
import { AppCommonModule } from 'src/app/common.module';
import { User } from 'src/app/models/user.model';

interface SqlEmployeeShift {
  id: string; firestore_user_id: string; firestore_site_id: string; firestore_project_id: string | null;
  shift_date: string; start_time: string | null; end_time: string | null; minutes?: number | null; unpaid_break_minutes: number;
  draft_revision: number; cancelled: boolean; note: string | null;
}
interface EmployeeSchedulesShift {
  source?: SqlEmployeeShift;
  netMinutes?: number;
  start?: Date;
  id: string;
  scheduleGuid: string;
  siteGuid: string | null;
  date: Date;
  dateLabel: string;
  timeLabel: string;
  durationLabel: string;
  siteName: string;
  teamLabel: string;
  workTypeLabel: string;
  notes: string;
  calendarType: 'shift' | 'allocation' | 'absence';
}

type ActualWorkDay = CalendarWorkDay;
type ActualCalendarEntry = CalendarActual & {siteName: string};

interface EmployeeSchedulesDay {
  iso: string;
  date: Date;
  label: string;
  shortDate: string;
  isToday: boolean;
  isWeekend: boolean;
  shifts: EmployeeSchedulesShift[];
  actualMinutes: number | null;
  actual: ActualCalendarEntry[];
}

interface EmployeeSchedulesWeekVm {
  periodLabel: string;
  shiftsCount: number;
  hoursLabel: string;
  nextShiftLabel: string;
  days: EmployeeSchedulesDay[];
  hasAnyShifts: boolean;
}

@Component({
  selector: 'app-employee-schedules',
  templateUrl: './employee-schedules.component.html',
  styleUrls: ['./employee-workspace.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule, EmployeeDurationPipe,
    AppCommonModule, IonModal, PlanningPanelComponent, ModalShellComponent,
  ],
})
export class EmployeeSchedulesComponent {
  private api = inject(WorkforceService);
  readonly weekShifts = signal<EmployeeSchedulesShift[]>([]);
  readonly actualDays = signal<ActualWorkDay[]>([]);
  readonly actualSiteNames = signal<Record<string, string>>({});
  readonly selectedActualId = signal<string | null>(null);
  readonly actualProjectName = signal('—');
  readonly projectedActual = signal<CalendarActual[]>([]);
  readonly actualEntries = computed<ActualCalendarEntry[]>(()=>this.projectedActual().map(a=>({...a, siteName:this.actualSiteNames()[a.siteId] || this.translate.instant('users.workspace.fallbacks.missingSite')})));

  readonly selectedActual = computed(()=>this.actualEntries().find(a=>a.id === this.selectedActualId()) || null);

  async selectActual(id: string) {
    this.selectedShiftId.set(null);this.selectedActualId.set(id);this.actualProjectName.set('—');
    const entry = this.selectedActual(), generation = this.generation;
    if (!entry?.projectId) {
return;
}
    try {
      const result = await this.api.request<{projects: {name: string}[]}>('options?' + new URLSearchParams({scope:'project-label', projectSite:entry.siteId, project:entry.projectId}).toString());
      if (generation === this.generation && this.selectedActualId() === id) {
this.actualProjectName.set(result.projects[0]?.name || '—');
}
    } catch { /* A missing project label must not hide persisted actual time. */ }
  }

  readonly loading = signal(false);
  readonly error = signal(false);
  readonly busy = signal(false);
  readonly canManage = signal(false);
  readonly editorDate = signal<string | null>(null);
  readonly changed = signal(false);
  private generation = 0;
  private translate = inject(TranslateService);

  user = input.required<User>();
  shifts = input.required<EmployeeSchedulesShift[]>();
  initialShiftDate = input<Date | null>(null);
  initialShiftSiteGuid = input<string | null>(null);

  schedulesChanged = output();

  weekStart = signal(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
  selectedShiftId = signal<string | null>(null);
  private initialShiftContextApplied = signal(false);

  private employeeShiftDetails = computed(() => this.weekShifts());

  scheduleWeekVm = computed<EmployeeSchedulesWeekVm>(() => {
    const shifts = this.employeeShiftDetails();
    const weekStart = this.weekStart();
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd }).map((date) => {
      const dayShifts = shifts.filter((shift) => format(shift.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'));
      const actual = this.actualEntries().filter(d=>d.date === format(date, 'yyyy-MM-dd'));
      return {
        actual,
        actualMinutes: actual.length ? actual.reduce((sum, s)=>sum + Math.round(s.minutes * 60000), 0) / 60000 : null,
        iso: format(date, 'yyyy-MM-dd'),
        date,
        label: format(date, 'EEE', { locale: fi }),
        shortDate: format(date, 'd.M.'),
        isToday: isToday(date),
        isWeekend: isWeekend(date),
        shifts: dayShifts,
      } satisfies EmployeeSchedulesDay;
    });
    const weekShifts = days.flatMap((day) => day.shifts);
    const totalHours = weekShifts.reduce((sum, shift) => {
      const hours = (shift.netMinutes ?? 0) / 60;
      return Number.isFinite(hours) ? sum + hours : sum;
    }, 0);
    const nextShift = [...shifts, ...this.shifts().filter(s => s.date > weekEnd || s.date < weekStart)].filter(s => (s.start || s.date) >= new Date()).sort((a, b) => (a.start || a.date).getTime() - (b.start || b.date).getTime())[0];
    return {
      periodLabel: `${format(weekStart, 'd.M.', { locale: fi })} - ${format(weekEnd, 'd.M.yyyy', { locale: fi })}`,
      shiftsCount: weekShifts.length,
      hoursLabel: `${Math.round(totalHours * 10) / 10} h`,
      nextShiftLabel: nextShift
        ? `${nextShift.dateLabel ?? ''} · ${nextShift.timeLabel ?? ''}`
        : this.translate.instant('users.workspace.fallbacks.noUpcomingShift'),
      days,
      hasAnyShifts: weekShifts.length > 0,
    } satisfies EmployeeSchedulesWeekVm;
  });

  selectedShift = computed<EmployeeSchedulesShift | null>(() => {
    if (this.selectedActual()) {
return null;
}
    const shifts = this.employeeShiftDetails();
    const selectedShiftId = this.selectedShiftId();
    if (selectedShiftId) {
      return shifts.find((shift) => shift.id === selectedShiftId) ?? null;
    }
    const weekStart = this.weekStart();
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return shifts.find((shift) => shift.date >= weekStart && shift.date <= weekEnd) ?? null;
  });

  private firstShiftDate = computed<Date | null>(() => {
    const shifts = this.employeeShiftDetails();
    return shifts.find((item) => item.date >= startOfToday())?.date ?? shifts[0]?.date ?? null;
  });

  constructor() {
    effect(() => {
 this.user(); this.weekStart(); void this.loadWeek();
});
    effect(() => {
      if (!this.initialShiftContextApplied()) {
        const initialShiftDate = this.initialShiftDate();
        if (initialShiftDate) {
          this.weekStart.set(startOfWeek(initialShiftDate, { weekStartsOn: 1 }));
          const siteGuid = this.initialShiftSiteGuid();
          const matchingShift = this.employeeShiftDetails().find((shift) =>
            format(shift.date, 'yyyy-MM-dd') === format(initialShiftDate, 'yyyy-MM-dd')
            && (!siteGuid || shift.siteGuid === siteGuid),
          ) ?? this.employeeShiftDetails().find((shift) =>
            format(shift.date, 'yyyy-MM-dd') === format(initialShiftDate, 'yyyy-MM-dd'));
          if (matchingShift) {
            this.selectedShiftId.set(matchingShift.id);
          }
        } else if (!this.scheduleWeekVm().hasAnyShifts && this.firstShiftDate()) {
          const firstShiftDate = this.firstShiftDate();
          if (firstShiftDate) {
            this.weekStart.set(startOfWeek(firstShiftDate, { weekStartsOn: 1 }));
          }
        }
        this.initialShiftContextApplied.set(true);
      }
    });
  }

  previousWeek() {
    this.weekStart.set(subWeeks(this.weekStart(), 1));
    this.selectedShiftId.set(null);this.selectedActualId.set(null);
  }

  nextWeek() {
    this.weekStart.set(addWeeks(this.weekStart(), 1));
    this.selectedShiftId.set(null);this.selectedActualId.set(null);
  }

  goToCurrentWeek() {
    this.weekStart.set(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
    this.selectedShiftId.set(null);this.selectedActualId.set(null);
  }

  selectShift(shiftId: string) {
    this.selectedActualId.set(null);this.selectedShiftId.set(shiftId);
  }

  @HostListener('window:focus') refreshOnFocus() {
 if (!this.loading() && !this.busy() && !this.editorDate()) {
void this.loadWeek();
}
}

  async loadWeek() {
    const generation = ++this.generation, user = this.user(), rangeStart = format(this.weekStart(), 'yyyy-MM-dd'), rangeEnd = format(endOfWeek(this.weekStart(), {weekStartsOn:1}), 'yyyy-MM-dd');
    this.loading.set(true);this.error.set(false);this.weekShifts.set([]);this.actualDays.set([]);this.projectedActual.set([]);this.canManage.set(false);
    try {
      const [calendar, options] = await Promise.all([
        readWorkforceCalendar(this.api, rangeStart, rangeEnd, user.guid),
        this.api.request<{role: string;sites: {id: string;name: string}[]}>('options?scope=filters'),

      ]);
      if (generation !== this.generation) {
return;
}
      this.actualSiteNames.set(Object.fromEntries(options.sites.map(site=>[site.id, site.name])));
      this.actualDays.set(calendar.workDays);this.projectedActual.set(calendar.actual);
      this.canManage.set(['ADMIN', 'MANAGER'].includes(options.role));
      this.weekShifts.set(calendar.planned.filter(r=>r.firestore_user_id === user.guid && !r.cancelled).map(r=>{
        const date = new Date(r.shift_date + 'T12:00:00'), start = r.start_time ? new Date(r.shift_date + 'T' + r.start_time) : date, end = r.end_time ? new Date(r.shift_date + 'T' + r.end_time) : null;
        const netMinutes = end ? (end.getTime() - start.getTime()) / 60000 - r.unpaid_break_minutes : Number(r.minutes || 0);
        return {id:r.id, scheduleGuid:r.id, siteGuid:r.firestore_site_id, date, start, dateLabel:format(date, 'd.M.yyyy'), timeLabel:r.start_time && r.end_time ? r.start_time.slice(0, 5) + ' - ' + r.end_time.slice(0, 5) : '—', durationLabel:r.start_time || r.minutes != null ? `${Math.round(netMinutes / 60 * 10) / 10} h` : '—', netMinutes, source:r.source === 'shift' ? r as SqlEmployeeShift : undefined,
          siteName:options.sites.find(site=>site.id === r.firestore_site_id)?.name || this.translate.instant('users.workspace.fallbacks.missingSite'),
          teamLabel:this.shifts().find(s=>s.siteGuid === r.firestore_site_id)?.teamLabel || this.translate.instant('users.workspace.fallbacks.noTeam'), workTypeLabel:this.shifts().find(s=>s.siteGuid === r.firestore_site_id)?.workTypeLabel || this.translate.instant('users.workspace.fallbacks.noArea'), notes:r.note || this.translate.instant('users.workspace.fallbacks.noDetails'), calendarType:'shift'};
      }));
      if (this.initialShiftDate() && this.initialShiftSiteGuid() && !this.selectedShiftId()){
        const match = this.weekShifts().find(s=>format(s.date, 'yyyy-MM-dd') === format(this.initialShiftDate(), 'yyyy-MM-dd') && s.siteGuid === this.initialShiftSiteGuid());if (match) {
this.selectedShiftId.set(match.id);
}
      }
    } catch {
if (generation === this.generation) {
this.error.set(true);
}
} finally {
if (generation === this.generation) {
this.loading.set(false);
}
}
  }

  async removeSelectedShift() {
    const shift = this.selectedShift()?.source;
    if (!shift || !this.canManage() || this.busy()) {
return;
}
    const confirmation = await Dialog.confirm({message:this.translate.instant('users.workspace.schedule.actions.deleteShiftConfirm'), okButtonTitle:this.translate.instant('general.delete'), cancelButtonTitle:this.translate.instant('general.cancel')});
    if (!confirmation.value) {
return;
}
    this.busy.set(true);this.error.set(false);
    try {
      await this.api.request('shifts', 'POST', {id:shift.id, version:shift.draft_revision, firestore_user_id:this.user().guid, firestore_site_id:shift.firestore_site_id, firestore_project_id:shift.firestore_project_id,
        start_date:shift.shift_date, end_date:shift.shift_date, start_time:shift.start_time?.slice(0, 5) || null, end_time:shift.end_time?.slice(0, 5) || null, unpaid_break_minutes:shift.unpaid_break_minutes, weekdays:[0, 1, 2, 3, 4, 5, 6], note:shift.note, cancelled:true});
      await this.saved();
    } catch {
this.error.set(true);
} finally {
this.busy.set(false);
}
  }

  addShiftForDay(date: Date) {
    if (this.canManage()) {
this.editorDate.set(format(date, 'yyyy-MM-dd'));
}
  }

  async saved() {
    this.editorDate.set(null);this.selectedShiftId.set(null);this.selectedActualId.set(null);this.changed.set(true);this.schedulesChanged.emit();await this.loadWeek();
  }
}
